/**
 * Plugin extension points for TileForge.
 *
 * Plugins declare capabilities they need. The API enforces these
 * at registration time — methods outside the declared set throw.
 *
 * Available capabilities:
 *   'objects'   — registerObjectType, getObjectTypes
 *   'overlays'  — registerOverlay
 *   'tools'     — registerTool
 *   'menu'      — registerMenuItems
 *   'state:read'  — getState (read-only proxy)
 *   'state:write' — getState (full mutable access)
 *   'render'    — requestRender
 */

/** All recognized capability names. */
export const CAPABILITIES = [
  'objects', 'overlays', 'tools', 'menu',
  'state:read', 'state:write', 'render',
];

export class PluginAPI {
  constructor(state, compositor, toolManager) {
    this._state = state;
    this._compositor = compositor;
    this._toolManager = toolManager;
    this._plugins = new Map();
    this._pluginCaps = new Map();   // pluginId → Set<string>
    this._objectTypes = new Map();
    this._menuItems = [];
    this._currentPluginId = null;
  }

  /**
   * Register a plugin with declared capabilities.
   * @param {string} id
   * @param {Object} plugin — must have init(api). May have destroy().
   * @param {string[]} [capabilities] — list of capability strings.
   *   If omitted, grants all capabilities (legacy compat, logs a warning).
   */
  registerPlugin(id, plugin, capabilities) {
    if (!capabilities) {
      console.warn(`[PluginAPI] Plugin '${id}' registered without capabilities — granting full access. Declare capabilities to restrict.`);
      capabilities = [...CAPABILITIES];
    }

    const caps = new Set(capabilities);
    // Validate capability names
    for (const c of caps) {
      if (!CAPABILITIES.includes(c)) {
        console.warn(`[PluginAPI] Plugin '${id}' declared unknown capability '${c}' — ignored.`);
        caps.delete(c);
      }
    }

    this._plugins.set(id, plugin);
    this._pluginCaps.set(id, caps);
    this._currentPluginId = id;

    // Give the plugin a scoped API proxy that enforces its capabilities
    const scopedAPI = new ScopedPluginAPI(this, id, caps);
    if (plugin.init) plugin.init(scopedAPI);

    this._currentPluginId = null;
  }

  /** Unregister a plugin. */
  unregisterPlugin(id) {
    const plugin = this._plugins.get(id);
    if (plugin?.destroy) plugin.destroy();
    this._plugins.delete(id);
    this._pluginCaps.delete(id);
  }

  /** Get capabilities for a registered plugin. */
  getPluginCapabilities(id) {
    return this._pluginCaps.get(id) || new Set();
  }

  // ─── Internal methods (called by ScopedPluginAPI) ──────────────

  _registerObjectType(name, schema) {
    this._objectTypes.set(name, schema);
  }

  _registerOverlay(overlay) {
    this._compositor.addOverlay(overlay);
  }

  _registerTool(name, tool) {
    this._toolManager.registerTool(name, tool);
  }

  _registerMenuItems(menuName, items, pluginLabel, pluginId) {
    this._menuItems.push({ menuName, items, pluginLabel, pluginId });
  }

  _getState(writable) {
    if (writable) return this._state;
    // Return a read-only proxy that blocks set/batch
    return new Proxy(this._state, {
      get(target, prop) {
        if (prop === 'set' || prop === 'batch') {
          return () => { throw new Error('Plugin does not have state:write capability'); };
        }
        const val = target[prop];
        return typeof val === 'function' ? val.bind(target) : val;
      },
    });
  }

  _requestRender() {
    this._compositor.requestRender();
  }

  // ─── Public queries (for core code, not plugins) ───────────────

  /** Get all registered object types. */
  getObjectTypes() {
    return this._objectTypes;
  }

  /**
   * Get extra menu items for a menu, grouped by plugin section.
   */
  getMenuItems(menuName) {
    const groups = this._menuItems.filter(m => m.menuName === menuName);
    if (groups.length === 0) return [];

    const result = [];
    for (const group of groups) {
      result.push({ separator: true });
      if (group.pluginLabel) {
        result.push({ sectionHeader: group.pluginLabel });
      }
      for (const item of group.items) {
        if (!item.separator) result.push(item);
      }
    }
    return result;
  }
}

/**
 * Scoped API proxy given to a specific plugin.
 * Only exposes methods matching the plugin's declared capabilities.
 */
class ScopedPluginAPI {
  constructor(api, pluginId, caps) {
    this._api = api;
    this._pluginId = pluginId;
    this._caps = caps;
  }

  _require(cap) {
    if (!this._caps.has(cap)) {
      throw new Error(`Plugin '${this._pluginId}' lacks '${cap}' capability`);
    }
  }

  registerObjectType(name, schema) {
    this._require('objects');
    this._api._registerObjectType(name, schema);
  }

  registerOverlay(overlay) {
    this._require('overlays');
    this._api._registerOverlay(overlay);
  }

  registerTool(name, tool) {
    this._require('tools');
    this._api._registerTool(name, tool);
  }

  registerMenuItems(menuName, items, pluginLabel = null) {
    this._require('menu');
    this._api._registerMenuItems(menuName, items, pluginLabel, this._pluginId);
  }

  getState() {
    const writable = this._caps.has('state:write');
    if (!writable) this._require('state:read');
    return this._api._getState(writable);
  }

  requestRender() {
    this._require('render');
    this._api._requestRender();
  }

  getObjectTypes() {
    this._require('objects');
    return this._api.getObjectTypes();
  }
}
